import React, {useEffect, useState} from 'react'
import {
  CreateCriticalitySettingsValuesModel,
  CriticalityValueModel,
} from '../../../../entities/component.entity'
import axios from 'axios'
import {toast} from 'react-toastify'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {useReduxAuth} from '../../../auth'
import {TELEMETRY_DATA_TYPE_NAME} from '../../../../constants/StatusConstants'
import {ApiResponseModel, CriticalitySettingsModel} from '../../../../entities/entities'
import CriticalitySettingsTable from '../../components/tables/CriticalitySettingsTable'

const CriticalitySettingsPage = () => {
  // toast alerts
  const notifyError = (msg: string) => toast.error(msg)
  const notifySuccess = (msg: string) => toast.success(msg)

  const {currentUser} = useReduxAuth()
  const [isCreateCriticalitySettingModalOpen, setIsCreateCriticalitySettingModalOpen] =
    useState(false)

  const [isEditCriticalitySettingModalOpen, setIsEditCriticalitySettingModalOpen] = useState(false)

  const [bloodPressureSettings, setBloodPressureSettings] = useState<CriticalitySettingsModel[]>([])
  const [heartRateSettings, setHeartRateSettings] = useState<CriticalitySettingsModel[]>([])
  const [currentValues, setCurrentValues] = useState<{
    sysValues: CriticalityValueModel[]
    diaValues: CriticalityValueModel[]
    pulseValues: CriticalityValueModel[]
  }>({
    sysValues: [],
    diaValues: [],
    pulseValues: [],
  })

  useEffect(() => {
    fetchCriticalitySettings()
  }, [])

  const fetchCriticalitySettings = async () => {
    try {
      const isNurse = currentUser?.isNurse
      const response = await axios.get<ApiResponseModel<{allSettings: CriticalitySettingsModel[]}>>(
        API_ENDPOINTS.GET_ALL_CRITICALITY_SETTINGS(`${isNurse ? 'is_nurse=1 ' : null}`)
      )
      const allSettings = response.data?.data?.allSettings || []

      // Explicitly define array types
      const bpSettings: CriticalitySettingsModel[] = []
      const hrSettings: CriticalitySettingsModel[] = []

      allSettings.forEach((setting) => {
        const type = setting.TelemetryType?.name
        if (
          type === TELEMETRY_DATA_TYPE_NAME.SYSTOLIC_PRESSURE ||
          type === TELEMETRY_DATA_TYPE_NAME.DIASTOLIC_PRESSURE
        ) {
          bpSettings.push(setting)
        } else if (type === TELEMETRY_DATA_TYPE_NAME.PULSE) {
          hrSettings.push(setting)
        }
      })

      setBloodPressureSettings(bpSettings)
      setHeartRateSettings(hrSettings)

      // Update currentValues with mapped values
      setCurrentValues({
        sysValues: mapCriticalityValues(bpSettings, TELEMETRY_DATA_TYPE_NAME.SYSTOLIC_PRESSURE),
        diaValues: mapCriticalityValues(bpSettings, TELEMETRY_DATA_TYPE_NAME.DIASTOLIC_PRESSURE),
        pulseValues: mapCriticalityValues(hrSettings, TELEMETRY_DATA_TYPE_NAME.PULSE),
      })
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || 'Something went wrong')
      } else if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('An unexpected error occurred')
      }
    }
  }

  // Function to map settings to { id, value }
  const mapCriticalityValues = (settings: CriticalitySettingsModel[], type: string) => {
    const mappedSettings = settings
      .filter((setting) => setting.TelemetryType?.name === type)
      .flatMap((setting) => [
        {id: setting.id, value: setting.min_value, is_max_value: false},
        {id: setting.id, value: setting.max_value, is_max_value: true},
      ])

    const reshaped = [mappedSettings[0], mappedSettings[2], mappedSettings[3], mappedSettings[1]]

    return reshaped
  }

  const handleSubmitSettingsCreation = async (
    values: CreateCriticalitySettingsValuesModel,
    resetForm: () => void
  ) => {
    try {
      const valuesReshaped = reshapeData(values)

      const response = await axios.post(API_ENDPOINTS.CREATE_NEW_CRITICALITY_SETTINGS(), {
        settings_data: valuesReshaped,
        is_nurse: currentUser?.isNurse ? true : null,
      })

      if (response.data.status === 'Success') {
        toast.success(response.data.message)
        resetForm()
      } else {
        toast.error(response.data.message || 'Failed to create criticality settings.')
      }
      setIsCreateCriticalitySettingModalOpen(false)
      fetchCriticalitySettings()
    } catch (error) {
      // Todo Refactor this
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || 'Something went wrong')
      } else if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('An unexpected error occurred')
      }
    }
  }

  const handleSubmitSettingsEdit = async (values: {
    sysValues: CriticalityValueModel[]
    diaValues: CriticalityValueModel[]
    pulseValues: CriticalityValueModel[]
  }) => {
    const settingsData = [...values?.sysValues, ...values?.diaValues, ...values?.pulseValues]

    try {
      const response = await axios.put(API_ENDPOINTS.CREATE_NEW_CRITICALITY_SETTINGS(), {
        settings_data: settingsData,
        is_nurse: currentUser?.isNurse ? true : null,
      })

      if (response.data.status === 'Success') {
        toast.success(response.data.message)
      } else {
        toast.error(response.data.message || 'Failed to update criticality settings.')
      }
      setIsEditCriticalitySettingModalOpen(false)
      fetchCriticalitySettings()
    } catch (error) {
      // Todo Refactor this
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || 'Something went wrong')
      } else if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('An unexpected error occurred')
      }
    }
  }

  const handleUpdateCriticalitySettings = async (selectedRowData: any) => {
    // setLoading(true)
    try {
      const response = await axios.put(API_ENDPOINTS.CREATE_NEW_CRITICALITY_SETTINGS(), {
        systolic: selectedRowData.systolic,
        diastolic: selectedRowData.diastolic,
        is_nurse: currentUser?.isNurse ? true : null,
      })

      // success message
      notifySuccess(response?.data?.message)
      // setUserData(items)
    } catch (error: any) {
      notifyError(error?.response?.data?.message)
    } finally {
      // setLoading(false)
    }
  }

  // reshape data function
  const reshapeData = (values: CreateCriticalitySettingsValuesModel) => {
    // Reassign values and combining save severity values together
    return {
      systolic_pressure: {
        critical: {
          min: values.sysValues[0],
          max: values.sysValues[3],
        },
        warning: {
          min: values.sysValues[1],
          max: values.sysValues[2],
        },
      },
      diastolic_pressure: {
        critical: {
          min: values.diaValues[0],
          max: values.diaValues[3],
        },
        warning: {
          min: values.diaValues[1],
          max: values.diaValues[2],
        },
      },
      pulse: {
        critical: {
          min: values.pulseValues[0],
          max: values.pulseValues[3],
        },
        warning: {
          min: values.pulseValues[1],
          max: values.pulseValues[2],
        },
      },
    }
  }

  return (
    <div>
      <div className='d-flex  justify-content-between align-items-end'>
        <h2>Health Alerts - Default Settings</h2>
      </div>

      {/* Criticality Settings Table */}
      <CriticalitySettingsTable
        allowEdit={false}
        criticalitySettings={bloodPressureSettings}
        onSave={handleUpdateCriticalitySettings}
      />
      <p className='mt-2 text-end'>Ref: American Heart Association</p>
    </div>
  )
}

export default CriticalitySettingsPage
